import { useDebouncer } from '@tanstack/react-pacer'
import { useEffect, useRef, useState } from 'react'

const DEBOUNCE_MS = 300

export type SearchSync = {
  /** What the box shows. */
  input: string
  /** The URL value the box is in sync with: what we last wrote or followed. */
  synced: string | undefined
  /** The previous `urlQuery`, to notice when it changes. */
  seenUrl: string | undefined
}

/**
 * The box's reaction to the URL's query (after a render). Our own write
 * coming back needs no action; any other change (Back/Forward, a shared
 * link) is external, and the box follows it. Returns `state` itself when
 * nothing changed.
 */
export function followUrl(
  state: SearchSync,
  urlQuery: string | undefined,
): SearchSync {
  if (urlQuery === state.seenUrl) return state
  if (urlQuery === state.synced) return { ...state, seenUrl: urlQuery }
  return { input: urlQuery ?? '', synced: urlQuery, seenUrl: urlQuery }
}

/**
 * What writing `value` to the URL should do, or null for nothing. History
 * semantics (docs/architecture.md, "Search"): the first query pushes an
 * entry, refinements and clearing replace it, so Back leaves the search.
 * `current` is the box's `input` now: a debounced write for text the box no
 * longer holds (the URL moved under it) is dropped.
 */
export function planCommit(
  value: string,
  current: SearchSync,
): { query: string | undefined; replace: boolean } | null {
  if (value !== current.input) return null
  const query = value.trim() || undefined
  if (query === current.synced) return null
  return { query, replace: !query || Boolean(current.synced) }
}

/**
 * A search box mirrored in the URL. `input` is what the box shows;
 * `onChange` debounces writing it to the URL through `navigate`, `submit`
 * writes it at once, and the box follows URL changes made elsewhere.
 */
export function useUrlSearchQuery(
  urlQuery: string | undefined,
  navigate: (query: string | undefined, replace: boolean) => void,
) {
  const [state, setState] = useState<SearchSync>({
    input: urlQuery ?? '',
    synced: urlQuery,
    seenUrl: urlQuery,
  })
  // Storing the previous prop in state is the way to adjust state when a prop
  // changes without an extra render pass through an effect.
  const followed = followUrl(state, urlQuery)
  if (followed !== state) setState(followed)

  // The debounced write fires from the render where the text was typed; it
  // reads what is current here (the target can change meanwhile, e.g. a tab
  // click) instead of its closure.
  const latest = useRef({ state, navigate })
  useEffect(() => {
    latest.current = { state, navigate }
  })

  const update = (patch: Partial<SearchSync>) => {
    latest.current.state = { ...latest.current.state, ...patch }
    setState(latest.current.state)
  }
  const commit = (value: string) => {
    const plan = planCommit(value, latest.current.state)
    if (!plan) return
    latest.current.navigate(plan.query, plan.replace)
    update({ synced: plan.query })
  }
  // Cancels any pending write when the component unmounts.
  const debouncer = useDebouncer(commit, { wait: DEBOUNCE_MS })

  return {
    input: state.input,
    onChange: (value: string) => {
      update({ input: value })
      debouncer.maybeExecute(value)
    },
    submit: () => {
      debouncer.cancel()
      commit(latest.current.state.input)
    },
  }
}
